<script lang="ts" setup>
import moment from 'moment';
import { DatePicker as VCalendarDatePicker } from 'v-calendar';
import type { DatePickerDate } from 'v-calendar/dist/types/src/use/datePicker.js';
import 'v-calendar/dist/style.css';

const props = defineProps<{
  modelValue: DatePickerDate
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: DatePickerDate): void;
}>();

const date = computed<DatePickerDate>({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value)
});

const year = computed<string>(() => {
  return moment(date.value).year().toString();
});

const today = computed<string>(() => {
  return moment(date.value).format('ddd, MMM D');
});

const attrs = {
  transparent: true,
  borderless: true,
  color: 'primary',
  'is-dark': { selector: 'html', darkClass: 'dark' }
};
</script>

<template>
  <div class="flex flex-col bg-company-1 p-4 pointer-events-none rounded-t">
    <span
      class="text-sm"
      data-test="year"
    >
      {{ year }}
    </span>
    <span
      class="text-3xl"
      data-test="today"
    >
      {{ today }}
    </span>
  </div>
  <VCalendarDatePicker
    v-model="date"
    class="calendar"
    locale="en-GB"
    v-bind="{ ...attrs, ...$attrs }"
  />
</template>

<style lang="scss">
::backdrop {
  background: rgb(var(--color-gray-900) / 0.75);
}

.calendar {

  .vc-title,
  .vc-weekday {
    text-transform: capitalize;
  }

  .vc-title {
    font-weight: normal;
    font-size: 1rem;
  }

  .vc-weekday {
    font-weight: normal;
    font-size: 0.75rem;
  }

  .vc-week,
  .vc-weekdays {
    column-gap: 0.5rem;
  }

  .vc-header {
    margin-top: 1rem;
  }

  .vc-weeks {
    padding: 0.5rem 1rem 1rem;
  }

  .vc-day-content:hover {
    filter: brightness(1.3);
    color: var(--company-text-color-1);
  }
}

:root {
  --vc-gray-50: var(--card-bg-color);
  --vc-gray-300: var(--card-border-color);
  --vc-gray-500: var(--card-subtext-color);
  --vc-gray-900: inherit;
}

.vc-primary {
  --vc-hover-bg: var(--company-color-1);
  --vc-accent-500: var(--company-color-1);
  --vc-accent-600: var(--company-color-1);
}
</style>
